import { useCallback, useEffect, useRef, useState } from 'react'

/**
 * The fictional clock, running 1:1 with wall time from an anchor.
 *
 * The engine's clock is real-time (doctrine-engine-clock-as-real-time): an hour
 * at the desk is an hour underground. So rather than poll for the time, we hold
 * an anchor — a known fictional instant and the wall instant we learned it — and
 * let the seconds run locally.
 *
 * Re-anchor whenever the engine tells us the truth: on a state read, on an
 * arriving message, and on the hour an advance actually landed.
 */
export function useFictionalClock(): {
  now: Date | null
  anchor: (fictionalIso: string | Date | null | undefined) => void
} {
  const fictional = useRef<number | null>(null)
  const wall = useRef<number>(0)
  const [now, setNow] = useState<Date | null>(null)

  const anchor = useCallback((value: string | Date | null | undefined) => {
    if (!value) return
    const t = value instanceof Date ? value.getTime() : Date.parse(value)
    if (Number.isNaN(t)) return
    // Forward-only. Out-of-order frames must never rewind the desk's clock.
    if (fictional.current !== null && t < fictional.current) return
    fictional.current = t
    wall.current = Date.now()
    setNow(new Date(t))
  }, [])

  useEffect(() => {
    const id = window.setInterval(() => {
      if (fictional.current === null) return
      setNow(new Date(fictional.current + (Date.now() - wall.current)))
    }, 1000)
    return () => window.clearInterval(id)
  }, [])

  return { now, anchor }
}

/** HH:MM in UTC — the fiction's own clock, never the reader's timezone. */
export function hhmm(d: Date | null | undefined): string {
  if (!d) return '--:--'
  return `${String(d.getUTCHours()).padStart(2, '0')}:${String(
    d.getUTCMinutes(),
  ).padStart(2, '0')}`
}

/**
 * HH:MM:SS — the transcript's stamp column (desk-grammar brief §2.1).
 *
 * A radio log stamps to the second; the minute alone reads as chat. Same UTC
 * basis as hhmm: this is the fiction's clock, not the reader's.
 */
export function hhmmss(d: Date | null | undefined): string {
  if (!d) return ''
  return `${hhmm(d)}:${String(d.getUTCSeconds()).padStart(2, '0')}`
}

/**
 * Parses what the operator writes down: an hour ("14:22", his time) or an
 * offset ("+40", "40", "40m").
 *
 * Whole minutes always — a contact is arranged at an hour, not at a
 * half-second, and a target carrying seconds reads as a hair's-breadth early
 * close to a desk writing HH:MM.
 */
export function parseWhen(text: string, now: Date | null): Date | null {
  if (!now) return null
  const t = text.trim()

  const offset = /^\+?(\d{1,4})\s*m?(?:in)?$/i.exec(t)
  if (offset) {
    const d = new Date(now.getTime() + parseInt(offset[1], 10) * 60000)
    d.setUTCSeconds(0, 0)
    return d
  }

  const clock = /^(\d{1,2}):(\d{2})$/.exec(t)
  if (clock) {
    const d = new Date(now)
    d.setUTCHours(+clock[1], +clock[2], 0, 0)
    if (d <= now) d.setUTCDate(d.getUTCDate() + 1)
    return d
  }

  return null
}
